import { formatApiError } from "./fetch";
import type { ChatPaywall, RecommendedProduct } from "./types";

export type ChatStreamEvent =
  | { type: "meta"; session_id: string; turn_id?: string }
  | { type: "text"; delta: string }
  | { type: "products"; products: RecommendedProduct[] }
  | { type: "next_action"; next_action: Record<string, unknown> }
  | {
      type: "paywall";
      paywall: ChatPaywall | unknown;
      tier?: string;
      credits_remaining?: number | null;
    }
  | {
      type: "tool_call";
      tool_call_id: string;
      tool_name: string;
      tool_input: Record<string, unknown>;
    }
  | { type: "tool_result"; tool_call_id: string; output: unknown }
  | { type: "offer_moment"; trigger: string }
  | { type: "done"; message_id?: string | null }
  | { type: "error"; message: string; retryable?: boolean; detail?: string };

export interface ChatStreamHandlers {
  onMeta?: (event: { session_id: string; turn_id?: string }) => void;
  onTextDelta: (delta: string) => void;
  onProducts?: (products: RecommendedProduct[]) => void;
  onNextAction?: (nextAction: Record<string, unknown>) => void;
  onPaywall?: (response: Record<string, unknown>) => void;
  onToolCall?: (event: {
    tool_call_id: string;
    tool_name: string;
    tool_input: Record<string, unknown>;
  }) => void;
  onToolResult?: (event: { tool_call_id: string; output: unknown }) => void;
  onOfferMoment?: (trigger: string) => void;
  onDone?: (event: { message_id?: string | null }) => void;
  onError?: (error: { message: string; retryable?: boolean; detail?: string }) => void;
}

export interface ChatStreamOptions {
  url: string;
  headers?: HeadersInit;
  body: unknown;
  handlers: ChatStreamHandlers;
  signal?: AbortSignal;
  /** Max ms to wait between stream chunks before giving up (default 90s). */
  idleTimeoutMs?: number;
}

const TEXT_DECODER = new TextDecoder("utf-8");
const EVENT_SEPARATOR = "\n\n";
const DATA_PREFIX = "data: ";

// A stalled SSE must never hang the caller: streamChat resolving is what lets
// the chat UI clear its loading state and re-enable the input. Before this
// watchdog existed, a hung stream left reader.read() pending forever and the
// chat input locked until a full page reload (observed 2026-06-11 while the
// edge silently dropped turns). 90s comfortably exceeds the slowest legitimate
// turn (tool round-trips + LLM stream) while bounding the failure mode.
const STREAM_IDLE_TIMEOUT_MS = 90_000;
const IDLE_TIMEOUT: unique symbol = Symbol("chat-stream-idle-timeout");

async function readWithIdleTimeout(
  reader: ReadableStreamDefaultReader<Uint8Array>,
  idleMs: number
): Promise<ReadableStreamReadResult<Uint8Array> | typeof IDLE_TIMEOUT> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      reader.read(),
      new Promise<typeof IDLE_TIMEOUT>((resolve) => {
        timer = setTimeout(() => resolve(IDLE_TIMEOUT), idleMs);
      }),
    ]);
  } finally {
    if (timer !== undefined) clearTimeout(timer);
  }
}

export async function streamChat(options: ChatStreamOptions): Promise<void> {
  const response = await openStreamResponse(options);
  if (!response) return;

  if (await handleJsonShortCircuit(response, options.handlers)) return;

  if (!response.body) {
    options.handlers.onError?.({ message: "Chat server returned no body.", retryable: true });
    return;
  }
  await consumeStream(response.body, options);
}

async function openStreamResponse(options: ChatStreamOptions): Promise<Response | null> {
  try {
    const response = await fetch(options.url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "text/event-stream",
        ...(options.headers ?? {}),
      },
      body: JSON.stringify(options.body),
      signal: options.signal,
    });
    if (!response.ok) {
      const detail = await responseErrorDetail(response);
      options.handlers.onError?.({
        message: detail ?? `Chat server returned ${response.status}. Please try again.`,
        retryable: response.status >= 500 || response.status === 429,
        detail,
      });
      return null;
    }
    return response;
  } catch (err) {
    if (options.signal?.aborted) return null;
    options.handlers.onError?.({
      message: "Failed to reach chat server. Check your connection and try again.",
      retryable: true,
      detail: err instanceof Error ? err.message : String(err),
    });
    return null;
  }
}

async function responseErrorDetail(response: Response): Promise<string | undefined> {
  const contentType = response.headers.get("content-type") ?? "";
  if (contentType.includes("application/json")) {
    const payload = await response.json().catch(() => null);
    if (payload !== null) return formatApiError(payload, response.status);
  }
  const text = await response.text().catch(() => "");
  return text.trim() || undefined;
}

async function handleJsonShortCircuit(
  response: Response,
  handlers: ChatStreamHandlers
): Promise<boolean> {
  const contentType = response.headers.get("content-type") ?? "";
  if (!contentType.includes("application/json")) return false;
  try {
    const payload = (await response.json()) as Record<string, unknown>;
    handlers.onPaywall?.(payload);
    handlers.onDone?.({});
  } catch (err) {
    handlers.onError?.({
      message: "Chat server returned a malformed paywall response.",
      retryable: false,
      detail: err instanceof Error ? err.message : String(err),
    });
  }
  return true;
}

async function consumeStream(
  body: ReadableStream<Uint8Array>,
  options: ChatStreamOptions
): Promise<void> {
  const reader = body.getReader();
  const idleMs = options.idleTimeoutMs ?? STREAM_IDLE_TIMEOUT_MS;
  let buffer = "";
  try {
    while (true) {
      const result = await readWithIdleTimeout(reader, idleMs);
      if (result === IDLE_TIMEOUT) {
        await reader.cancel("chat stream idle timeout").catch(() => {});
        options.handlers.onError?.({
          message: "The connection went quiet — please send that again.",
          retryable: true,
          detail: `no stream data for ${idleMs}ms`,
        });
        return;
      }
      const { done, value } = result;
      if (done) break;
      buffer += TEXT_DECODER.decode(value, { stream: true });
      buffer = drainChatStreamEvents(buffer, options.handlers);
    }
    if (buffer.trim().length > 0) dispatchChatStreamEvent(buffer, options.handlers);
  } catch (err) {
    if (options.signal?.aborted) return;
    options.handlers.onError?.({
      message: "Connection dropped mid-stream.",
      retryable: true,
      detail: err instanceof Error ? err.message : String(err),
    });
  } finally {
    try {
      reader.releaseLock();
    } catch {
      /* reader may already be released */
    }
  }
}

export function drainChatStreamEvents(buffer: string, handlers: ChatStreamHandlers): string {
  let separatorIndex: number;
  while ((separatorIndex = buffer.indexOf(EVENT_SEPARATOR)) !== -1) {
    const rawEvent = buffer.slice(0, separatorIndex);
    buffer = buffer.slice(separatorIndex + EVENT_SEPARATOR.length);
    dispatchChatStreamEvent(rawEvent, handlers);
  }
  return buffer;
}

export function parseChatStreamEvent(rawEvent: string): ChatStreamEvent | null {
  let dataPayload = "";
  for (const line of rawEvent.split("\n")) {
    if (line.startsWith(DATA_PREFIX)) dataPayload += line.slice(DATA_PREFIX.length);
  }
  if (!dataPayload || dataPayload === "[DONE]") return null;
  try {
    return JSON.parse(dataPayload) as ChatStreamEvent;
  } catch {
    return { type: "text", delta: dataPayload };
  }
}

const DISPATCHERS: {
  [K in ChatStreamEvent["type"]]: (
    event: Extract<ChatStreamEvent, { type: K }>,
    handlers: ChatStreamHandlers
  ) => void;
} = {
  meta: (event, handlers) =>
    handlers.onMeta?.({ session_id: event.session_id, turn_id: event.turn_id }),
  text: (event, handlers) => handlers.onTextDelta(event.delta),
  products: (event, handlers) => handlers.onProducts?.(event.products),
  next_action: (event, handlers) => handlers.onNextAction?.(event.next_action),
  paywall: (event, handlers) =>
    handlers.onPaywall?.({
      paywall: event.paywall,
      tier: event.tier,
      credits_remaining: event.credits_remaining ?? null,
    }),
  tool_call: (event, handlers) =>
    handlers.onToolCall?.({
      tool_call_id: event.tool_call_id,
      tool_name: event.tool_name,
      tool_input: event.tool_input,
    }),
  tool_result: (event, handlers) =>
    handlers.onToolResult?.({
      tool_call_id: event.tool_call_id,
      output: event.output,
    }),
  offer_moment: (event, handlers) => handlers.onOfferMoment?.(event.trigger),
  done: (event, handlers) => handlers.onDone?.({ message_id: event.message_id }),
  error: (event, handlers) =>
    handlers.onError?.({
      message: event.message,
      retryable: event.retryable,
      detail: event.detail,
    }),
};

export function dispatchChatStreamEvent(rawEvent: string, handlers: ChatStreamHandlers): void {
  const parsed = parseChatStreamEvent(rawEvent);
  if (!parsed) return;
  const handler = DISPATCHERS[parsed.type];
  if (handler) {
    (handler as (e: ChatStreamEvent, h: ChatStreamHandlers) => void)(parsed, handlers);
  }
}
